import React, { useEffect, useState } from "react"
import styled from "@emotion/styled"
import * as layouts from "layouts"
import * as authzc from "authzcached"
import * as httpx from "httpx"
import * as profiles from "profiles"
import * as feedback from "brandguard/api/feedback"

const Container = styled(layouts.containers.flex)`
	flex-direction: column;
	gap: 12px;
	padding: 16px;
	background: ${layouts.theme.colors.grey.lightest};
	border-radius: 8px;
`

const SectionLabel = styled.span`
	font-size: 12px;
	font-weight: 600;
	color: ${layouts.theme.colors.grey.dark50};
	text-transform: uppercase;
`

const TextArea = styled.textarea`
	padding: 8px 10px;
	border: 1px solid ${layouts.theme.colors.grey.dark50alpha20};
	border-radius: 4px;
	font-size: 13px;
	resize: vertical;
	min-height: 60px;
	font-family: inherit;
	&:focus {
		outline: none;
		border-color: ${layouts.theme.colors.blue.blue};
	}
`

const Input = styled.input`
	padding: 8px 10px;
	border: 1px solid ${layouts.theme.colors.grey.dark50alpha20};
	border-radius: 4px;
	font-size: 13px;
	&:focus {
		outline: none;
		border-color: ${layouts.theme.colors.blue.blue};
	}
`

const Select = styled.select`
	padding: 8px 10px;
	border: 1px solid ${layouts.theme.colors.grey.dark50alpha20};
	border-radius: 4px;
	font-size: 13px;
	background: ${layouts.theme.colors.white};
	cursor: pointer;
	&:focus {
		outline: none;
		border-color: ${layouts.theme.colors.blue.blue};
	}
`

const ButtonRow = styled(layouts.containers.flex)`
	flex-direction: row;
	gap: 8px;
	flex-wrap: wrap;
`

const ActionButton = styled(layouts.buttons.outline)`
	font-size: 12px;
	padding: 8px 12px;
	white-space: nowrap;
`

const Result = styled(layouts.containers.flex)<{ success: boolean }>`
	padding: 8px 10px;
	border-radius: 4px;
	font-size: 12px;
	background: ${(props) => (props.success ? layouts.theme.colors.success : layouts.theme.colors.red.cinnabar)};
	color: ${layouts.theme.colors.white};
`

const EmailSection = styled(layouts.containers.flex)`
	flex-direction: column;
	gap: 8px;
	padding: 10px;
	background: ${layouts.theme.colors.white};
	border-radius: 4px;
	border: 1px dashed ${layouts.theme.colors.grey.dark50alpha20};
`

interface props {
	eventType: feedback.AssetType
	eventIds: string[]
	uploadedBy?: string
	title?: string
	onSubmitted?: (resp: feedback.SubmitResponse) => void
}

export function FeedbackPanel(props: props): JSX.Element {
	const { eventType, eventIds, uploadedBy, title, onSubmitted } = props
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const permission = authzaccount.current

	const isSingle = eventIds.length === 1
	const [comment, setComment] = useState("")
	const [emailExpanded, setEmailExpanded] = useState(false)
	const [recipient, setRecipient] = useState("")
	const [subject, setSubject] = useState("")
	const [loading, setLoading] = useState(false)
	const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

	const [accountProfiles, setAccountProfiles] = useState<profiles.api.proto.Profile[]>([])

	useEffect(() => {
		if (!emailExpanded || accountProfiles.length > 0) return
		const retry = httpx.autoretry()
		const req = profiles.api.searches.zero({ limit: 100n, enabled: profiles.api.StatusFilters.Enabled })
		const p = retry
			.wrap(() => profiles.api.search(req, authzc.bearer(authzaccount)))
			.then(async (resp) => {
				const eligible = (resp.items ?? []).filter((p) => p.email && p.email.trim() !== "")
				const uploader =
					eligible.find((p) => p.id === uploadedBy) ??
					(uploadedBy ? await profiles.caching.current(uploadedBy, authzc.bearer(authzaccount)).catch(() => undefined) : undefined)
				setAccountProfiles(uploader && !eligible.includes(uploader) && uploader.email ? [uploader, ...eligible] : eligible)
				setRecipient((current) => current || uploader?.email || "")
			})
			.catch(httpx.errors.cancellation(() => undefined))
			.catch((cause) => console.warn("unable to load profiles for email", cause))
		return p.cancel
	}, [emailExpanded])

	const submit = (action: feedback.ClientAction): void => {
		setLoading(true)
		setResult(null)
		const retry = httpx.autoretry()
		retry
			.wrap(() =>
				feedback.submit(
					{
						event_type: eventType,
						event_ids: eventIds,
						selected_types: [],
						comment,
						client_action: action,
						email:
							action === "email"
								? { recipient_email: recipient, subject, message: "" }
								: undefined,
					},
					authzc.bearer(authzaccount),
				),
			)
			.then((resp) => {
				if (action === "copy") {
					navigator.clipboard.writeText(resp.formatted_markdown).catch((cause) => console.warn("clipboard write failed", cause))
				}
				const success = action !== "email" || resp.email_sent
				const message =
					action === "email"
						? resp.email_sent
							? `Feedback submitted; email sent to ${recipient}`
							: `Feedback submitted; email failed: ${resp.email_error}`
						: action === "copy"
						? "Feedback submitted and copied to clipboard"
						: "Feedback submitted"
				setResult({ success, message })
				if (success) setComment("")
				onSubmitted?.(resp)
			})
			.catch((cause) => {
				console.error("submit feedback failed", cause)
				setResult({ success: false, message: "Failed to submit feedback" })
			})
			.finally(() => setLoading(false))
	}

	const emailClick = (): void => {
		if (!emailExpanded) {
			setEmailExpanded(true)
			return
		}
		if (!recipient) {
			setResult({ success: false, message: "Recipient email is required" })
			return
		}
		submit("email")
	}

	if (!permission.brandguard_review) return <></>

	return (
		<Container>
			<SectionLabel>{title ?? (isSingle ? "Feedback" : `Feedback on ${eventIds.length} assets`)}</SectionLabel>

			<layouts.containers.flex flexDirection="column" gridGap="6px">
				<SectionLabel>Comment</SectionLabel>
				<TextArea
					value={comment}
					onChange={(e) => setComment(e.target.value)}
					placeholder="Optional free-text feedback…"
				/>
			</layouts.containers.flex>

			{emailExpanded && (
				<EmailSection>
					<SectionLabel>Recipient</SectionLabel>
					<Select value={recipient} onChange={(e) => setRecipient(e.target.value)}>
						<option value="">Select a recipient</option>
						{accountProfiles.map((profile) => (
							<option key={profile.id} value={profile.email}>
								{profile.display ? `${profile.display} (${profile.email})` : profile.email}
							</option>
						))}
					</Select>
					<SectionLabel>Subject (optional)</SectionLabel>
					<Input
						value={subject}
						onChange={(e) => setSubject(e.target.value)}
						placeholder="Brand Compliance Review - Feedback Received"
					/>
				</EmailSection>
			)}

			{result && <Result success={result.success}>{result.message}</Result>}

			<ButtonRow>
				<ActionButton disabled={loading} onClick={() => submit("submit")}>
					Submit Feedback
				</ActionButton>
				<ActionButton disabled={loading} onClick={() => submit("copy")}>
					Submit + Copy
				</ActionButton>
				<ActionButton disabled={loading} onClick={emailClick}>
					{emailExpanded ? "Submit + Send Email" : "Submit + Email"}
				</ActionButton>
			</ButtonRow>
		</Container>
	)
}
